import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, SerpTitleTruncatedDetails } from "@/types/technicalAudit";

interface SerpTitleTruncatedCardProps {
	issue: PageIssue;
}

export function SerpTitleTruncatedCard({ issue }: SerpTitleTruncatedCardProps) {
	const details = issue.issue_details as SerpTitleTruncatedDetails;
	const percentage = Math.min((details.pixel_width / details.max_pixel_width) * 100, 100);
	// Approximate truncation point based on ratio
	const truncIdx = Math.floor(details.current_title.length * (details.max_pixel_width / details.pixel_width));
	const visiblePart = details.current_title.slice(0, truncIdx);
	const truncatedPart = details.current_title.slice(truncIdx);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">SERP Title Truncated</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This title exceeds {details.max_pixel_width}px and will be cut off in search results. Users won't see the full title.
					</p>
				</div>
			</div>

			{/* SERP Preview */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						SERP Preview
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="p-4 bg-card rounded-lg border border-border">
						<p className="text-lg leading-snug">
							<span className="text-blue-700 dark:text-blue-400">{visiblePart}</span>
							{truncatedPart && (
								<span className="text-red-400 line-through">{truncatedPart}</span>
							)}
							<span className="text-muted-foreground">...</span>
						</p>
					</div>

					{/* Pixel width bar */}
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<span className={cn("text-lg font-bold", "text-amber-700 dark:text-amber-400")}>
								{details.pixel_width}px
							</span>
							<span className="text-sm text-muted-foreground">
								/ {details.max_pixel_width}px max
							</span>
						</div>
						<div className="relative">
							<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
								<div
									className="h-full rounded-full transition-all bg-amber-500"
									style={{ width: `${percentage}%` }}
								/>
							</div>
							<div
								className="absolute top-0 w-0.5 h-5 -mt-1 bg-gray-600"
								style={{ left: "100%" }}
							/>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
